import DownloadIcon from "@mui/icons-material/FileDownload";
import FormDialog from "components/FormsUI/FormDialog";
import { CircularCenteredLoader } from "components/Loaders";
import { makeStyles } from "components/mui-core";
import { bytesToFileSize, formatDate, monDayAndTimeFormat } from "helpers";
import React from "react";
import { allCsvDownloadsSlice } from "redux/csvDownloads/allCsvDownloadsSlice";
import { analyticsCsvDownloadsSlice } from "redux/csvDownloads/analyticsCsvDownloadsSlice";
import {
  getAllCsvDownloadsList,
  isAllDownloadsModalOpened,
  TDownloadKind,
} from "redux/csvDownloads/selectors";
import { trafficCsvDownloadsSlice } from "redux/csvDownloads/trafficCsvDownloadsSlice";
import { ICompletedMetadata, IDownload } from "redux/csvDownloads/types";
import { useSelector } from "redux/hooks";
import { useAppDispatch } from "redux/store";
import loadCsvReportWhenReady from "services/reporter/csvDownload/loadCsvReportWhenReady";
import { dangerColor, primaryColor, warningColor } from "vendor/colors";
import { Muted, MutedInline } from "vendor/components";
import { formatRemainingTime } from "./utils";

const useStyles = makeStyles()({
  dialog: {
    minWidth: "60%",
  },
  dialogContent: {
    padding: "0 20px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  trigger: {
    cursor: "pointer",
  },
  kindChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  analyticsChip: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  trafficChip: {
    backgroundColor: "#f3e5f5",
    color: `${primaryColor[7]}`,
  },
});

type TReportKind = "analytics" | "traffic";
const REDUX: Record<
  TReportKind,
  {
    slice: typeof analyticsCsvDownloadsSlice | typeof trafficCsvDownloadsSlice;
  }
> = {
  analytics: {
    slice: analyticsCsvDownloadsSlice,
  },
  traffic: {
    slice: trafficCsvDownloadsSlice,
  },
};

export function AllDownloadsModalTrigger({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const dispatch = useAppDispatch();
  const { classes, cx } = useStyles();

  return (
    <>
      <div
        className={cx(classes.trigger, className)}
        onClick={() =>
          dispatch(allCsvDownloadsSlice.actions.toggleModal(true))
        }>
        {children}
      </div>
    </>
  );
}

function getKindChipClass(kind: TDownloadKind, classes: any) {
  switch (kind) {
    case "analytics":
      return classes.analyticsChip;
    case "traffic":
      return classes.trafficChip;
    default:
      return "";
  }
}

function UnifiedDownloadDetail({
  item,
}: {
  item: IDownload & { kind: TDownloadKind };
}) {
  const dispatch = useAppDispatch();
  const { classes, cx } = useStyles();
  const slice = REDUX[item.kind].slice;

  const resumeDownload = () => {
    dispatch(
      slice.actions.resumeDownload({
        id: item.id,
      })
    );

    loadCsvReportWhenReady({
      userFriendlyFilename: item.userFriendlyFilename ?? item.fileName,
      reportUrl: item.fileUrl,
      reportFilename: item.fileName,
      errorLogUrl: item.errorLogUrl,
      errorLogFilename: item.errorLogFilename,
    })
      .then((metadata: ICompletedMetadata) => {
        dispatch(
          slice.actions.completeDownload({
            id: item.id,
            metadata,
          })
        );
      })
      .catch(() => {
        dispatch(
          slice.actions.failDownload({
            id: item.id,
          })
        );
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <span
          className={cx(
            classes.kindChip,
            getKindChipClass(item.kind, classes)
          )}>
          {item.kind}
        </span>
      </div>
      <div>
        <MutedInline>File name: </MutedInline>
        {"  "}
        {item.userFriendlyFilename ?? item.fileName}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <div>
          <MutedInline>Started on:</MutedInline>
          {"  "}
          {formatDate(new Date(item.addedDatetime), monDayAndTimeFormat)}
        </div>
        {item.completedDatetime && (
          <div>
            <MutedInline>Completed on:</MutedInline>
            {"  "}
            {formatDate(new Date(item.completedDatetime), monDayAndTimeFormat)}
          </div>
        )}
        {item.completedMetadata?.contentLength && (
          <div>
            <MutedInline>File size: </MutedInline>
            {"  "}
            {bytesToFileSize(item.completedMetadata?.contentLength)}
          </div>
        )}

        {!!item.estimatedTime && item.status === "loading" && (
          <div>
            <MutedInline>
              Estimated completition{" "}
              {formatRemainingTime(
                new Date(item.addedDatetime),
                item.estimatedTime
              )}
            </MutedInline>
          </div>
        )}
      </div>
      {item.status === "loading" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            color: primaryColor[0],
            gap: 8,
          }}>
          Preparing file for download
          <div style={{ width: 20 }}>
            <CircularCenteredLoader size={18} />
          </div>
        </div>
      ) : item.status === "completed" ? (
        <a
          href={item.fileUrl}
          download={item.userFriendlyFilename ?? item.fileName}
          style={{
            all: "unset",
            color: primaryColor[0],
            cursor: "pointer",
            marginLeft: -4,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DownloadIcon style={{ fontSize: 24 }} />
            Click to download
          </div>
        </a>
      ) : item.status === "paused" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            color: warningColor[0],
            gap: 8,
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={resumeDownload}>
            <DownloadIcon style={{ fontSize: 24 }} />
            Resume download
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            color: dangerColor[0],
            gap: 8,
          }}>
          {item.errorLogUrl ? (
            <a
              href={item.errorLogUrl}
              download={item.userFriendlyErrorLogFilename}
              style={{
                all: "unset",
                color: dangerColor[0],
                cursor: "pointer",
                marginLeft: -4,
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <DownloadIcon style={{ fontSize: 24 }} />
                Click to download error log
              </div>
            </a>
          ) : (
            <span>File download failed</span>
          )}
        </div>
      )}
    </div>
  );
}

function AllDownloadsModal() {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const list = useSelector(getAllCsvDownloadsList);

  return (
    <FormDialog
      isOpen
      onClose={() => dispatch(allCsvDownloadsSlice.actions.toggleModal(false))}
      classes={{ root: classes.dialog }}
      cancelButtonLabel="Close"
      dialogTitle={
        <span>
          CSV downloads <Muted>in last 48 hours</Muted>
        </span>
      }>
      <div className={classes.dialogContent}>
        {list?.length ? (
          list.map((item) => (
            <UnifiedDownloadDetail
              key={`${item.kind}-${item.id}`}
              item={item}
            />
          ))
        ) : (
          <Muted>No downloads in last 48 hours...</Muted>
        )}
      </div>
    </FormDialog>
  );
}

function AllCsvDownloadsDialog() {
  const isModalOpened = useSelector(isAllDownloadsModalOpened);

  return <>{isModalOpened && <AllDownloadsModal />}</>;
}

export default AllCsvDownloadsDialog;
